import React, { useState, useCallback } from "react";
import { CalculatorSliderField } from "@/components/design-system/Calculator/CalculatorSliderField";
import { CalculatorStatCard } from "@/components/design-system/Calculator/CalculatorStatCard";
import { CalculatorBarChart } from "@/components/design-system/Calculator/CalculatorBarChart";
import { CalculatorFooter } from "@/components/design-system/Calculator/CalculatorFooter";
import styles from "@/components/design-system/Calculator/Calculator.module.css";
import {
  type WealthCalculatorInputs,
  type WealthCalculatorResults,
  DEFAULT_INPUTS,
  formatCompactDollar,
  formatCount,
  calcResults,
} from "./wealthCalculator.utils";

export const WealthCalculator: React.FC = () => {
  const [inputs, setInputs] = useState<WealthCalculatorInputs>(DEFAULT_INPUTS);
  const results: WealthCalculatorResults = calcResults(inputs);

  const handleChange = useCallback((field: keyof WealthCalculatorInputs, value: number) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  }, []);

  return (
    <div
      className="bg-primary-500 rounded-7 relative mx-auto w-full max-w-235 overflow-hidden font-sans"
      style={{ boxShadow: "0 4px 40px 0 #361637, 0 32px 80px rgba(45,18,48,0.45), 0 8px 24px rgba(0,0,0,0.15)" }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 75% 60% at -5% 105%, rgba(255,255,255,0.12) 0%, transparent 55%), radial-gradient(ellipse 55% 45% at 105% -5%, rgba(45,18,48,0.6) 0%, transparent 55%)" }}
      />

      <div className="relative z-1 p-[28px_28px_22px]">
        <div className="mb-5.5 flex flex-wrap items-center justify-between gap-3">
          <img src="/logo.png" alt="Bravo" className="h-8 w-auto brightness-0 invert" />
          <div className="text-[11px] flex items-center gap-1.75 rounded-full border border-white/30 bg-white/10 px-3.5 py-1.25 font-bold tracking-[0.07em] text-white uppercase">
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full bg-white ${styles.badgeDot}`} />
            AUM Opportunity Calculator
          </div>
        </div>

        <p className="mx-auto mb-4.5 max-w-125 text-center text-sm leading-normal text-white/75">
          How much untapped AUM is sitting inside your employer relationships? Enter your book of business below and see what Bravo can surface.
        </p>

        <div className={styles.calcGrid}>
          {/* FORM PANEL */}
          <div className="rounded-[18px] border border-white/60 bg-[#faf6fb] p-[22px_20px_24px]">
            <div className="text-primary-500 text-[11px] mb-4 text-center font-extrabold tracking-[0.08em] uppercase">
              Your Book of Business
            </div>
            <div className={`${styles.sectionHeading} ${styles.sectionHeadingFirst}`}>Core Inputs</div>

            <CalculatorSliderField label="Plan sponsors / employer relationships" tooltip="The number of employer plans your firm manages or advises on." value={inputs.sponsors} min={50} max={1000} step={10} minLabel="50" maxLabel="1,000" onChange={(val) => handleChange("sponsors", val)} />
            <CalculatorSliderField label="Average employees per plan" tooltip="Average number of participants across your employer relationships." value={inputs.empPerPlan} min={50} max={5000} step={50} minLabel="50" maxLabel="5,000" onChange={(val) => handleChange("empPerPlan", val)} />
            <CalculatorSliderField label="Annual job change rate" tooltip="~15% annual turnover rate is typical across industries (BLS)." value={inputs.turnoverRate} min={5} max={40} step={1} suffix="%" minLabel="5%" maxLabel="40%" onChange={(val) => handleChange("turnoverRate", val)} />
            <CalculatorSliderField label="Average retirement account balance" tooltip="National average ~$88K per employee (Vanguard, 2024)." value={inputs.avgBalance} min={20000} max={300000} step={5000} prefix="$" minLabel="$20K" maxLabel="$300K" onChange={(val) => handleChange("avgBalance", val)} />
            <CalculatorSliderField label="Target AUM conversion rate" tooltip="Industry benchmark for warm referrals is ~15%." value={inputs.convRate} min={5} max={40} step={1} suffix="%" minLabel="5%" maxLabel="40%" onChange={(val) => handleChange("convRate", val)} />
            <CalculatorSliderField label="Average fee rate" tooltip="Advisory fee rate applied to converted AUM. Typical range 0.5%–1.5%." value={inputs.feeRate} min={0.25} max={2} step={0.05} suffix="%" minLabel="0.25%" maxLabel="2%" onChange={(val) => handleChange("feeRate", val)} />

            <button
              className="rounded-[10px] mt-4 block w-full cursor-pointer border-none p-3 text-sm font-extrabold text-white transition-[opacity,transform] duration-200 hover:-translate-y-px hover:opacity-88"
              style={{ background: "linear-gradient(37.77deg, #B662A6 -20%, #5B245C 70%)" }}
            >
              Calculate AUM Opportunity
            </button>
          </div>

          {/* RESULTS PANEL */}
          <div className="rounded-[18px] border border-white/18 bg-white/7 p-[22px_18px]">
            <div className="text-[11px] mb-4 text-center font-extrabold tracking-[0.08em] text-white/60 uppercase">Results</div>

            <div className="mb-3.5 grid grid-cols-2 gap-1.75">
              <CalculatorStatCard label="Total Participants"       value={formatCount(results.totalParticipants)}   subtext="across all plans"              />
              <CalculatorStatCard label="Assets in Motion"         value={formatCompactDollar(results.assetsInMotion)}      subtext="from job changes"              />
              <CalculatorStatCard label="Flagged Opportunities"    value={formatCompactDollar(results.flaggedOpportunities)} subtext="surfaced by Bravo"            />
              <CalculatorStatCard label="Estimated AUM Converted"  value={formatCompactDollar(results.aumConverted)}        subtext="new assets under management" highlight />
            </div>

            <hr className="my-3.25 border-t border-none border-white/12" />

            <div className={`${styles.statCard} ${styles.statCardHighlight} ${styles.statCardCentered} mb-3.5`}>
              <div className={styles.statCardLabel}>Annual Recurring Revenue</div>
              <div className={styles.statCardValue} style={{ fontSize: 28 }}>{formatCompactDollar(results.annualRevenue)}</div>
              <div className={styles.statCardSubtext}>converted AUM × fee rate</div>
            </div>

            <hr className="my-3.25 border-t border-none border-white/12" />

            <div className="text-[10px] mb-2 text-center font-bold tracking-[0.07em] text-white/50 uppercase">5-Year AUM Projection</div>

            <CalculatorBarChart data={results.projection} formatValue={formatCompactDollar} maxBarHeight={100} chartHeight={120} />

            <p className="text-[11px] mt-2.5 text-center leading-normal text-white/40 italic">
              For illustration purposes only. Projections are based on the inputs provided and do not constitute a guarantee of results. Actual outcomes will vary.
            </p>
          </div>
        </div>
      </div>

      <CalculatorFooter
        sources={[
          <><em>Capitalize / Center for Retirement Research</em> — $2.13T in forgotten 401(k)s across 31.9M accounts (Sept 2025). <a href="https://capitalizefinancial.com" target="_blank" rel="noreferrer" className="text-white/85">capitalizefinancial.com</a></>,
          <>Vanguard — <em>How America Saves 2024</em>: avg. retirement account balance ~$88K. <a href="https://vanguard.com" target="_blank" rel="noreferrer" className="text-white/85">vanguard.com</a></>,
          <>BLS — Median employee tenure 3.9 years (Jan 2024); avg. 12 job changes per career. <a href="https://bls.gov" target="_blank" rel="noreferrer" className="text-white/85">bls.gov</a> &nbsp;·&nbsp; ICI — $6.77T in money market funds (Dec 2024). <a href="https://ici.org" target="_blank" rel="noreferrer" className="text-white/85">ici.org</a></>,
        ]}
      />
    </div>
  );
};
